const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "/api";
const storageKey = "zeimu-saas.auth";

interface AuthPayload {
  idToken: string;
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  expiresAt: number;
}

export interface AuthSession {
  getIdToken(): { getJwtToken(): string };
  isValid(): boolean;
}

export async function signIn(email: string, password: string): Promise<AuthSession> {
  const response = await fetch(`${apiBaseUrl}/auth/login`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, password })
  });
  const payload = await readJson<AuthPayload | { message?: string }>(response);
  if (!response.ok || !("idToken" in payload)) {
    throw new Error(("message" in payload && payload.message) || "ログインに失敗しました。");
  }
  const sessionPayload = {
    ...payload,
    expiresAt: Date.now() + ((payload.expiresIn ?? 3600) - 60) * 1000
  };
  localStorage.setItem(storageKey, JSON.stringify(sessionPayload));
  return toSession(sessionPayload);
}

export function getCurrentSession(): Promise<AuthSession | null> {
  const raw = localStorage.getItem(storageKey);
  if (!raw) return Promise.resolve(null);
  try {
    const payload = JSON.parse(raw) as AuthPayload;
    const session = toSession(payload);
    if (!session.isValid()) {
      signOut();
      return Promise.resolve(null);
    }
    return Promise.resolve(session);
  } catch {
    signOut();
    return Promise.resolve(null);
  }
}

export function signOut() {
  localStorage.removeItem(storageKey);
}

export async function requestPasswordReset(email: string): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/auth/forgot-password`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email })
  });
  if (!response.ok) {
    const payload = await readJson<{ message?: string }>(response);
    throw new Error(payload.message || "パスワード再設定メールの送信に失敗しました。");
  }
}

export async function confirmPasswordReset(email: string, code: string, newPassword: string): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/auth/confirm-forgot-password`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, code, newPassword })
  });
  if (!response.ok) {
    const payload = await readJson<{ message?: string }>(response);
    throw new Error(payload.message || "パスワード更新に失敗しました。");
  }
}

function toSession(payload: AuthPayload): AuthSession {
  return {
    getIdToken: () => ({ getJwtToken: () => payload.idToken }),
    isValid: () => Boolean(payload.idToken) && Date.now() < payload.expiresAt
  };
}

async function readJson<T>(response: Response): Promise<T> {
  try {
    return (await response.json()) as T;
  } catch {
    return {} as T;
  }
}
